import api from './client';

export interface SupplierStatus {
  badge: 'fresh' | 'requested' | 'overdue' | 'stale' | 'ok';
  label: string;
}

export interface OilSupplier {
  id: number;
  name: string;
  email: string | null;
  counterparty_id: number | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  source_type: 'email' | 'web';
  last_price_at: string | null;
  last_request_at: string | null;
  days_since_update: number | null;
  status: SupplierStatus;
}

export interface UpdateSupplierBody {
  counterparty_id?: number | null;
  email?: string | null;
}

export interface OilPrice {
  supplier_id: number;
  article: string | null;
  qty: number | null;
  ordered: number;
  price: number;
  warehouse?: string | null;
  origin: string | null;
  updated_at: string;
}

export interface OilProductSize {
  id: number;
  container_size: string;
  prices: OilPrice[];
}

export interface OilProduct {
  id: number;
  catalog_id: number | null;
  product_types: string[];
  brand: string;
  model_name: string;
  sizes: OilProductSize[];
}

export interface RequestPricelistBody {
  type: 'full' | 'partial';
  size_ids?: number[];
}

export interface CheckEmailsResponse {
  checked: number;
  imported: number;
  forwarded: number;
  details: string[];
}

export interface CartItem {
  size_id: number;
  supplier_id: number;
  origin: string | null;
  qty: number;
  saved_price?: number | null;
  saved_available?: number | null;
}

export interface CartChangeItem {
  size_id: number;
  supplier_id: number;
  origin: string | null;
  cart_qty: number;
  old_price: number | null;
  new_price: number | null;
  old_available: number | null;
  new_available: number | null;
  change_type: 'removed' | 'price_changed' | 'qty_changed' | 'price_and_qty_changed';
  product_name: string;
  size_label: string;
  supplier_name: string;
}

export interface CartValidationResult {
  changes: CartChangeItem[];
  ok: boolean;
}

export interface CartResolveItem {
  size_id: number;
  supplier_id: number;
  origin: string | null;
}

export interface CartResolveBody {
  keep: CartResolveItem[];
  remove: CartResolveItem[];
  task_id?: number | null;
}

export interface OilOrderItem {
  id: number;
  size_id: number;
  supplier_id: number;
  supplier_name: string;
  product_types: string[];
  brand: string;
  model_name: string;
  container_size: string;
  article: string | null;
  origin: string | null;
  qty: number;
  price: number;
}

export interface OrderEmailStatus {
  supplier_id: number;
  supplier_name: string;
  status: 'sending' | 'sent' | 'error';
  error_message: string | null;
}

export interface OilOrderResponse {
  id: number;
  created_at: string;
  created_by: number;
  created_by_name: string;
  total_sum: number;
  total_qty: number;
  note: string | null;
  items: OilOrderItem[];
  emails: OrderEmailStatus[];
}

export interface RepeatOrderResponse {
  added: number;
  skipped: number;
  reduced: number;
  message: string;
}

export interface OilSettingsResponse {
  type_order: string[];
  brand_order: string[];
  model_order: string[];
  neva_config: { url?: string; login?: string; password?: string };
  forward_email: string;
  fresh_days: number;
}

export interface OilSettingsBody {
  type_order?: string[];
  brand_order?: string[];
  model_order?: string[];
  neva_config?: { url?: string; login?: string; password?: string };
  forward_email?: string;
  fresh_days?: number;
}

export const oilOrdersApi = {
  getSuppliers: () =>
    api.get<OilSupplier[]>('/oil-orders/suppliers').then(r => r.data),

  getProducts: (params?: { search?: string; product_type?: string; brand?: string }) =>
    api.get<OilProduct[]>('/oil-orders/products', { params }).then(r => r.data),

  requestPricelist: (supplierId: number, body: RequestPricelistBody) =>
    api.post<{ ok: boolean; sent_to: string }>(`/oil-orders/request/${supplierId}`, body).then(r => r.data),

  checkEmails: () =>
    api.post<CheckEmailsResponse>('/oil-orders/check-emails').then(r => r.data),

  updateSupplier: (supplierId: number, body: UpdateSupplierBody) =>
    api.put<OilSupplier>(`/oil-orders/suppliers/${supplierId}`, body).then(r => r.data),

  uploadPricelist: (supplierId: number, file: File, fullReplace: boolean) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ ok: boolean; task_id: number; status: string }>(
      `/oil-orders/upload/${supplierId}?full_replace=${fullReplace}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ).then(r => r.data);
  },

  getCart: () =>
    api.get<CartItem[]>('/oil-orders/cart').then(r => r.data),

  upsertCartItem: (body: CartItem) =>
    api.put('/oil-orders/cart', body).then(r => r.data),

  clearCart: () =>
    api.delete('/oil-orders/cart').then(r => r.data),

  placeOrder: () =>
    api.post<OilOrderResponse>('/oil-orders/cart/place').then(r => r.data),

  validateCart: () =>
    api.post<CartValidationResult>('/oil-orders/cart/validate').then(r => r.data),

  resolveCart: (body: CartResolveBody) =>
    api.post('/oil-orders/cart/resolve', body).then(r => r.data),

  getOrders: () =>
    api.get<OilOrderResponse[]>('/oil-orders/orders').then(r => r.data),

  deleteOrder: (orderId: number) =>
    api.delete(`/oil-orders/orders/${orderId}`).then(r => r.data),

  repeatOrder: (orderId: number) =>
    api.post<RepeatOrderResponse>(`/oil-orders/orders/${orderId}/repeat`).then(r => r.data),

  resendOrderEmail: (orderId: number, supplierId: number) =>
    api.post(`/oil-orders/orders/${orderId}/resend/${supplierId}`).then(r => r.data),

  clearPrices: (supplierId: number) =>
    api.delete<{ ok: boolean; deleted: number }>(`/oil-orders/suppliers/${supplierId}/prices`).then(r => r.data),

  getSettings: () =>
    api.get<OilSettingsResponse>('/oil-orders/settings').then(r => r.data),

  updateSettings: (body: OilSettingsBody) =>
    api.put<OilSettingsResponse>('/oil-orders/settings', body).then(r => r.data),

  fetchNeva: () =>
    api.post<{ ok: boolean; task_id: number; status: string }>(
      '/oil-orders/fetch-neva',
    ).then(r => r.data),

  // Feedback (сообщения об ошибках в данных)
  createFeedback: (body: { message: string; product_ids: number[] }) =>
    api.post<{ id: number; status: string }>('/oil-orders/feedback', body).then(r => r.data),

  listFeedback: (status?: string) =>
    api.get<OilFeedbackItem[]>('/oil-orders/feedback', { params: status ? { status } : {} }).then(r => r.data),

  updateFeedback: (id: number, body: { status: string }) =>
    api.put<{ id: number; status: string }>(`/oil-orders/feedback/${id}`, body).then(r => r.data),
};

export interface OilFeedbackItem {
  id: number;
  user_id: number | null;
  username: string | null;
  message: string;
  product_ids: number[];
  status: string;
  created_at: string | null;
  resolved_at: string | null;
}
